import React, { FunctionComponent, memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../../i18n/useLanguage";
import {
  SubmodelOption,
  useGetVehicleSubmodels,
} from "../../../queries/useGetVehicleSubmodels";
import { useSearchForm } from "../../../stores/hooks/useSearchForm";
import { Dropdown } from "../../../ui/Dropdown";
import { Input } from "../../../ui/Input/Input";
import { Menu } from "../../../ui/Menu/Menu";
import { MenuButton } from "../../../ui/Menu/MenuButton";
import { Spinner } from "../../../ui/Spinner";
import { TabPanels, TabPanelsContext } from "../../../ui/TabPanels/TabPanels";
import { Toggle } from "../../../ui/Toggle";
import { ArrowLeftIcon } from "../../../ui/icons/ArrowLeftIcon";
import { CarIcon } from "../../../ui/icons/CarIcon";
import { ChevronRightIcon } from "../../../ui/icons/ChevronRightIcon";
import { getSubmodelDisplayName } from "../../../util/getSubmodelDisplayName";
import { usePlacement } from "./usePlacement";

const hasSubmenu = (option?: SubmodelOption): boolean => {
  return Number(option?.data.vehicleSubmodelTiresizeArray.length) > 1;
};

const UncachedInput: FunctionComponent = () => {
  const tabsContextRef = useRef<TabPanelsContext>();
  const { t } = useTranslation();
  const [language] = useLanguage();
  const [
    {
      vehicleModelName,
      vehicleSubmodel,
      vehicleSubmodelSelected,
      vehicleSubmodelTiresizeId,
    },
    { setSelectedVehicleSubmodel, setVehicleSubmodel },
  ] = useSearchForm();

  const {
    data: submodels,
    isLoading,
    selectedSubmodel,
  } = useGetVehicleSubmodels();

  const disabled = !vehicleModelName;
  const shouldAutoOpen = !vehicleSubmodelTiresizeId && !!submodels && !disabled;

  // reset
  useEffect(() => {
    if (!vehicleSubmodel) {
      setSelectedVehicleSubmodel(null);
      if (tabsContextRef.current?.index) {
        tabsContextRef.current?.prev();
      }
    }
  }, [vehicleModelName]);

  const selecteTiresize =
    selectedSubmodel?.data.vehicleSubmodelTiresizeArray.find(
      (item) => item.vehicleSubmodelTiresizeId === vehicleSubmodelTiresizeId
    );

  const submodelDisplayName = getSubmodelDisplayName(
    vehicleModelName,
    vehicleSubmodelSelected
  );

  return (
    <Toggle>
      <Input
        after={isLoading ? <Spinner /> : <ChevronRightIcon />}
        aria-label="selectedTrim"
        before={<CarIcon />}
        disabled={disabled}
        id="trim-combo"
        name="trim"
        placeholder={t("searchForm.selectTrim")}
        readOnly
        role="combobox"
        value={selecteTiresize?.formattedTireSize || ""}
      />
      <Dropdown
        aria-labelledby="trim-combo"
        id="trim-combo-dropdown"
        placement={usePlacement()}
        role="menu"
        stopOutsidePropagation
        visible={shouldAutoOpen}
      >
        <TabPanels contextRef={tabsContextRef} defaultIndex={0}>
          <Menu id="trim-combo-dropdown--submodel">
            {submodels?.map((option) => {
              const hasPopup = hasSubmenu(option);
              return (
                <MenuButton
                  onClick={(): void => {
                    setSelectedVehicleSubmodel(option.label);
                    if (hasPopup) {
                      tabsContextRef.current?.next();
                    } else {
                      setVehicleSubmodel(
                        option.label,
                        option.data.vehicleSubmodelTiresizeArray[0]
                          .vehicleSubmodelTiresizeId
                      );
                    }
                  }}
                  selected={
                    option.label.toLowerCase() ===
                    vehicleSubmodel?.toLowerCase()
                  }
                  aria-haspopup={hasPopup}
                  aria-label="selectSubmodel"
                  className="py-2 block"
                  key={option.value}
                >
                  {getSubmodelDisplayName(vehicleModelName, option.label)}
                  <small className="block truncate font-thin max-w-[10rem]">
                    {option.description}
                  </small>
                </MenuButton>
              );
            })}
          </Menu>
          {Boolean(
            selectedSubmodel?.data.vehicleSubmodelTiresizeArray.length
          ) && (
            <Menu id="trim-combo-dropdown--trim">
              <MenuButton
                onClick={(): void => {
                  setSelectedVehicleSubmodel(null);
                  tabsContextRef.current?.prev();
                }}
                aria-haspopup={true}
                aria-label="backToSubmodelList"
              >
                <ArrowLeftIcon className="shrink-0 w-4 -translate-x-3" />
                <span className="line-clamp-2">{submodelDisplayName}</span>
              </MenuButton>
              {selectedSubmodel?.data.vehicleSubmodelTiresizeArray.map(
                ({
                  formattedTireSize,
                  secondaryFormattedTireSize,
                  tiresizeTypeDescription,
                  vehicleSubmodelTiresizeId,
                }) => {
                  return (
                    <MenuButton
                      onClick={(): void => {
                        setVehicleSubmodel(
                          selectedSubmodel?.label,
                          vehicleSubmodelTiresizeId
                        );
                      }}
                      selected={
                        vehicleSubmodelTiresizeId ===
                        selecteTiresize?.vehicleSubmodelTiresizeId
                      }
                      aria-label="selectTireSize"
                      className="py-2 block pl-12"
                      key={vehicleSubmodelTiresizeId}
                    >
                      <span className="font-semibold block">
                        {[formattedTireSize, secondaryFormattedTireSize]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                      <small className="font-thin">
                        {tiresizeTypeDescription[language] ||
                          tiresizeTypeDescription.en}
                      </small>
                    </MenuButton>
                  );
                }
              )}
            </Menu>
          )}
        </TabPanels>
      </Dropdown>
    </Toggle>
  );
};

export const TrimInput = memo(UncachedInput);
